<script setup lang="ts">
import { AxiosError } from 'axios' // Import AxiosError
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import userService from '@/services/userService'
import { usesKitEmailDomain } from '@/utils/kitEmailDomains'

definePage({
  meta: {
    title: 'pages.user.login.title',
    requiresAuth: false,
  },
})

const username = ref('')
const password = ref('')
const isLoading = ref(false)
const showConfirmationMailButton = ref(false)
const errorMessage = ref('')
const { login: loginUser } = useUser()
const router = useRouter()
const route = useRoute()
const returnUrl = route.query.returnUrl
const { t } = useI18n()

const loginErrorDetailMap: Record<string, string> = {
  accountislockedout: 'accountLockedOut',
  emailconfirmationrequired: 'notAllowed',
  invalidloginattempt: 'invalidLoginAttempt',
  notallowed: 'notAllowed',
}

const resolveLoginErrorKey = (detail: string): string => {
  const normalizedDetail = detail.toLowerCase().replace(/[^a-z0-9]/g, '')
  return loginErrorDetailMap[normalizedDetail] ?? 'genericLoginError'
}

// A sign-in through KIT, bwIDM or Google that does not complete redirects back here with the
// reason attached rather than failing as a server error, so the message has to come from the URL.
const remoteSignInErrorMap: Record<string, string> = {
  cancelled: 'signInCancelled',
  expired: 'signInExpired',
  failed: 'signInFailed',
}

const remoteSignInError = Array.isArray(route.query.authError)
  ? route.query.authError[0]
  : route.query.authError

// Kept apart from errorMessage, which belongs to the credential form below: the attempt that
// failed started at one of the provider buttons, so the explanation has to appear there.
const remoteSignInErrorMessage = ref('')

if (typeof remoteSignInError === 'string' && remoteSignInError.length > 0) {
  remoteSignInErrorMessage.value = t(
    `pages.user.login.errors.${remoteSignInErrorMap[remoteSignInError] ?? 'signInFailed'}`,
  )
}

// Resend confirmation mail state
const isResending = ref(false)
const resendError = ref(false)
const resendSuccess = ref(false)

const login = async () => {
  if (usesKitEmailDomain(username.value)) {
    errorMessage.value = t('pages.user.login.errors.kitEmailDomain')
    showConfirmationMailButton.value = false
    return
  }

  try {
    showConfirmationMailButton.value = false
    isLoading.value = true
    await loginUser(username.value, password.value)

    // Redirect to returnUrl or default location
    const redirectPath = (returnUrl as string) || '/'
    router.push(redirectPath)
  } catch (error: unknown) {
    // Explicitly type error as unknown
    console.error('Login error:', error)

    // Check if it's an AxiosError with a response and data
    if (error instanceof AxiosError && error.response?.data) {
      const errorData = error.response.data as any // Type assertion might be needed depending on exact API response structure

      // Handle structured error response using i18n
      if (typeof errorData.detail === 'string' && errorData.detail.trim().length > 0) {
        const errorKey = resolveLoginErrorKey(errorData.detail)
        errorMessage.value = t(`pages.user.login.errors.${errorKey}`)
        if (errorKey === 'notAllowed') {
          showConfirmationMailButton.value = true
        }
      } else {
        errorMessage.value = t('pages.user.login.errors.genericLoginError')
      }
    } else {
      errorMessage.value = t('pages.user.login.errors.genericLoginError')
    }
  } finally {
    isLoading.value = false
  }
}

const resendConfirmationMail = async () => {
  resendError.value = false
  resendSuccess.value = false
  isResending.value = true
  try {
    const result = await userService.resendConfirmationEmail(username.value)
    if (result) {
      resendSuccess.value = true
    } else {
      resendError.value = true
    }
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.data?.detail) {
      resendError.value = true
    }
  } finally {
    isResending.value = false
  }
}
</script>

<template>
  <div style="max-width: 400px" class="mycard px-4 py-2">
    <div class="mb-4 flex flex-row justify-between">
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-200">
        {{ t('pages.user.login.title') }}
      </h1>
    </div>
    <p class="mb-4 text-muted">
      {{ t('pages.user.login.intro') }}
      <br />
      {{ t('pages.user.login.kitRecommendation') }}
    </p>

    <AppAlert v-if="remoteSignInErrorMessage" color="error" :closable="false" class="mb-4">
      {{ remoteSignInErrorMessage }}
    </AppAlert>

    <a :href="returnUrl ? `/api/user/oidc-login?returnUrl=${returnUrl}` : '/api/user/oidc-login'">
      <UButton class="mt-2 mb-2 w-full text-white!">
        <KITIcon class="h-4 w-4" />
        {{ t('pages.user.login.kitAccountButton') }}
      </UButton>
    </a>
    <a :href="returnUrl ? `/api/user/bwidm-login?returnUrl=${returnUrl}` : '/api/user/bwidm-login'">
      <UButton
        :label="t('pages.user.login.otherUniversitiesButton')"
        icon="i-lucide-graduation-cap"
        class="mb-4 w-full"
        color="info"
        variant="outline"
      />
    </a>
    <div class="mb-4 flex flex-row gap-3">
      <a
        :href="
          returnUrl ? `/api/user/google-login?returnUrl=${returnUrl}` : '/api/user/google-login'
        "
        class="w-full"
      >
        <UButton class="w-full" color="neutral" variant="outline">
          <img
            src="@/assets/images/google-favicon-2025.svg"
            alt=""
            aria-hidden="true"
            class="size-4 shrink-0"
          />
          <span>Google</span>
        </UButton>
      </a>
      <a href="/api/user/microsoft-login" class="w-full">
        <UButton class="w-full" color="neutral" variant="outline">
          <img
            src="@/assets/images/microsoft-logo.svg"
            alt=""
            aria-hidden="true"
            class="size-4 shrink-0"
          />
          <span>Microsoft</span>
        </UButton>
      </a>
    </div>

    <div class="align-center my-4 flex flex-row items-center justify-center gap-3 dark:opacity-50">
      <hr style="flex: 1" />
      <div class="text-center">{{ t('pages.user.login.or') }}</div>
      <hr style="flex: 1" />
    </div>

    <div class="mb-4">
      <div class="flex flex-row justify-between">
        <span class="font-semibold text-muted">
          {{ t('pages.user.login.localAccount') }}
        </span>
        <router-link to="/user/register" class="font-semibold text-blue-700 dark:text-blue-400">
          {{ t('pages.user.login.registerButton') }}
        </router-link>
      </div>
    </div>
    <div class="flex flex-col gap-3">
      <AppAlert v-if="errorMessage" color="error" :closable="false" class="mb-2">
        {{ errorMessage }}
      </AppAlert>
      <div v-if="showConfirmationMailButton && !resendSuccess" class="mb-2 flex flex-col gap-2">
        <UButton
          :label="t('pages.user.login.resendConfirmation')"
          size="sm"
          color="info"
          class="-mt-2"
          :loading="isResending"
          :disabled="isResending || !username"
          @click="resendConfirmationMail"
          variant="ghost"
        />
        <AppAlert v-if="resendSuccess" color="success" :closable="false" class="mb-0">
          {{ t('pages.user.login.resendConfirmationSuccess') }}
        </AppAlert>
        <AppAlert v-if="resendError" color="error" :closable="false" class="mb-0">
          {{ t('pages.user.login.resendConfirmationError') }}
        </AppAlert>
      </div>
      <AppFloatField variant="on">
        <UInput id="username" v-model="username" class="w-full" autocomplete="email" />
        <label for="username">{{ t('fields.user.email') }}</label>
      </AppFloatField>
      <AppFloatField variant="on">
        <UInput
          id="password"
          v-model="password"
          type="password"
          class="w-full"
          autocomplete="password"
        />
        <label for="password">{{ t('fields.user.password') }}</label>
      </AppFloatField>
      <div class="flex flex-row gap-2">
        <div class="flex-1">
          <UButton
            data-testid="login-submit"
            :label="t('common.labels.signIn')"
            class="w-full"
            :disabled="!username || !password || isLoading"
            :loading="isLoading"
            @click="login"
          />
        </div>
        <div class="flex-1">
          <router-link to="/user/forgot-password" class="w-full text-xs">
            <UButton
              size="sm"
              :label="t('pages.user.login.forgotPassword')"
              color="neutral"
              variant="ghost"
            />
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>
